import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import OrgWizardStepper from '../../components/OrgWizardStepper';
import { SaveIcon, CheckCircleIcon } from '../../assets/icons';
import Breadcrumbs from '../../components/Breadcrumbs';
import OrganizationDetailsStep from './createOrg/OrganizationDetailsStep';
import DefineBoundaryStep, {
  type HierarchyView,
  type SelectedArea,
  type SavedArea,
} from './createOrg/DefineBoundaryStep';
import BuildHierarchyStep, { type Level } from './createOrg/BuildHierarchyStep';
import DataImportStep from './createOrg/DataImportStep';
import {
  initialOrgValues,
  orgValidationSchema,
  toOrgPayload,
  fromOrgResponse,
  type OrgFormValues,
} from './createOrg/types';
import {
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useCreateEcScopeBulkMutation,
  useCreateLevelsBulkMutation,
  useGetOrganizationQuery,
  useGetEcScopesQuery,
  useGetLevelsQuery,
} from '../../features/organizations/organizationsApi';
import { useGetEcLevelsQuery } from '../../features/territory/territoryApi';

interface StepMeta {
  label: string;
  title: string;
  subtitle: string;
}

const STEPS_META: StepMeta[] = [
  {
    label: 'Create New',
    title: 'Create Organization',
    subtitle: 'Register a new entity within the organizational hierarchy.',
  },
  {
    label: 'Define Boundary',
    title: 'Define Operational Boundary',
    subtitle: 'Choose the administrative node to define election commission jurisdiction.',
  },
  {
    label: 'Build Hierarchy',
    title: 'Organization Hierarchy Configuration',
    subtitle: 'Configure and sequence the administrative hierarchy of your organization.',
  },
  {
    label: 'Structure Explorer',
    title: 'Organization Hierarchy Data Import',
    subtitle: 'Manage and visualize the political structure across all levels.',
  },
];
// Note: the 5th step ("Create Organization User") is intentionally hidden for now;
// completing step 4 returns to the Organizations list.

const LAST_STEP = STEPS_META.length - 1;

/** Single-page 5-step wizard. Steps stay mounted so values persist when you go back to edit. */
export default function CreateOrganizationWizard() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // The org id can come from either the edit route (/:id/edit) or the create-continued
  // route (/new/:id). Having an id means we load + prefill the org; only the /edit route
  // is treated as "edit mode" for labels — the create flow never shows an "edit" tag.
  const { id: editId } = useParams<{ id: string }>();
  const hasId = Boolean(editId);
  const isEdit = pathname.endsWith('/edit');
  const [searchParams, setSearchParams] = useSearchParams();
  // Step lives in the URL as a 1-based number (?step=2 = second step) so a refresh resumes here.
  const [step, setStep] = useState(() => {
    const idx = Number(searchParams.get('step')) - 1;
    return Number.isInteger(idx) && idx >= 0 && idx <= LAST_STEP ? idx : 0;
  });

  // Advance/return to a step and mirror it into the URL (1-based, so refresh restores it).
  const goToStep = (next: number) => {
    setStep(next);
    const params = new URLSearchParams(searchParams);
    params.set('step', String(next + 1));
    setSearchParams(params, { replace: true });
  };
  // Live selection from step 2, and the snapshot confirmed for display in step 3.
  const [liveSelection, setLiveSelection] = useState<HierarchyView[]>([]);
  const [confirmedSelection, setConfirmedSelection] = useState<HierarchyView[]>([]);
  // Ordered organization-level names from step 3 (used to prefill step 4's template).
  const [hierarchyLevels, setHierarchyLevels] = useState<string[]>([]);
  // Structured step-3 levels ({id,name,parentId}) — POSTed to the level bulk API on Save.
  const [levelsData, setLevelsData] = useState<Level[]>([]);
  // EC areas checked in step 2 — POSTed to the bulk scope API on Confirm.
  const [liveAreas, setLiveAreas] = useState<SelectedArea[]>([]);
  // Id returned by the step-1 create-org POST — the scope call is keyed to it.
  const [organizationId, setOrganizationId] = useState<number | null>(null);

  const [createOrganization, { isLoading: isCreating }] = useCreateOrganizationMutation();
  const [updateOrganization, { isLoading: isUpdating }] = useUpdateOrganizationMutation();
  const isSaving = isCreating || isUpdating;
  const [createEcScopeBulk, { isLoading: isSavingScope }] = useCreateEcScopeBulkMutation();
  const [createLevelsBulk, { isLoading: isSavingLevels }] = useCreateLevelsBulkMutation();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [scopeError, setScopeError] = useState<string | null>(null);
  const [levelsError, setLevelsError] = useState<string | null>(null);
  const { data: ecLevels } = useGetEcLevelsQuery(undefined, { skip: step < 1 });

  // With an id (edit or create-continued): load the org and prefill step 1.
  const { data: editOrg } = useGetOrganizationQuery(editId!, { skip: !hasId });
  useEffect(() => {
    if (editId) setOrganizationId(Number(editId));
  }, [editId]);

  // Load the org's saved boundaries to prefill step 2's tree — only once step 2 is reached.
  const { data: savedScopes } = useGetEcScopesQuery(
    { organization: editId! },
    { skip: !hasId || step < 1 },
  );
  const preselectedAreas = useMemo<SavedArea[]>(
    () =>
      (savedScopes ?? []).map((s) => ({
        levelRank: s.ec_location_level_id,
        areaCode: s.area_code,
      })),
    [savedScopes],
  );

  // Load the org's saved levels to prefill step 3's tree — only once step 3 is reached.
  const { data: savedLevels } = useGetLevelsQuery(
    { organization: editId! },
    { skip: !hasId || step < 2 },
  );
  const initialLevels = useMemo<Level[]>(
    () =>
      (savedLevels ?? []).map((l) => ({
        id: String(l.id),
        name: l.level_name,
        parentId: l.parent != null ? String(l.parent) : null,
      })),
    [savedLevels],
  );

  // Resolve an EC level's id from /territory/ec-level/ (by name, then rank).
  // That table can be empty; the scope API's ec_location_level_id is the level
  // RANK by convention (Assembly=4, Booth=9…), so fall back to the rank.
  const resolveLevelId = (area: SelectedArea): number => {
    const list = ecLevels ?? [];
    const byName = list.find((l) => l.level_name?.toLowerCase() === area.levelName.toLowerCase());
    if (byName) return byName.id;
    const byRank = list.find((l) => l.level_rank === area.levelRank);
    if (byRank) return byRank.id;
    return area.levelRank;
  };

  const finish = (values: OrgFormValues) => {
    // eslint-disable-next-line no-console
    console.log('Create organization', values);
    navigate('/admin/organizations');
  };

  const formik = useFormik<OrgFormValues>({
    initialValues: editOrg ? fromOrgResponse(editOrg) : initialOrgValues,
    enableReinitialize: true,
    validationSchema: orgValidationSchema,
    onSubmit: finish,
  });

  const meta = STEPS_META[step];
  const isLast = step === LAST_STEP;

  // Step 1: validate the org-details form, POST it, then advance on success.
  const saveOrganization = async () => {
    setSaveError(null);
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      // Reveal every field's error message.
      formik.setTouched(
        Object.keys(initialOrgValues).reduce((acc, k) => ({ ...acc, [k]: true }), {}),
        false,
      );
      return;
    }
    try {
      const payload = toOrgPayload(formik.values);
      if (hasId && editId) {
        await updateOrganization({ id: editId, patch: payload }).unwrap();
        setOrganizationId(Number(editId));
        goToStep(1);
      } else {
        const created = await createOrganization(payload).unwrap();
        if (created?.id == null) {
          setSaveError('Organization was created but no id was returned. Please reopen it to continue.');
          return;
        }
        const newId = Number(created.id);
        setOrganizationId(newId);
        // Carry the new id in the create route (no "edit" tag) at step 2, so a refresh resumes here.
        // The component instance is reused across this navigation, so advance the step explicitly.
        setStep(1);
        navigate(`/admin/organizations/new/${newId}?step=2`, { replace: true });
      }
    } catch (err) {
      const status = (err as { status?: number | string })?.status;
      setSaveError(
        typeof status === 'number'
          ? `Could not save organization (server responded ${status}).`
          : 'Could not save organization. Please check your connection and try again.',
      );
    }
  };

  // Step 2: POST the checked EC boundaries, then snapshot the selection and advance.
  const confirmBoundary = async () => {
    setScopeError(null);
    if (!organizationId) {
      setScopeError('Please complete step 1 (create the organization) first.');
      return;
    }
    const areas = liveAreas
      .map((a) => ({ ec_location_level_id: resolveLevelId(a), area_code: a.areaCode }))
      .filter(
        (a): a is { ec_location_level_id: number; area_code: string } =>
          a.ec_location_level_id != null && !!a.area_code,
      );
    if (areas.length === 0) {
      setScopeError('Select at least one boundary to continue.');
      return;
    }
    try {
      await createEcScopeBulk({ organization: organizationId, areas, replace: true }).unwrap();
      // Snapshot what was selected so steps 3 & 4 can show it, then advance.
      setConfirmedSelection(liveSelection);
      goToStep(2);
    } catch (err) {
      const status = (err as { status?: number | string })?.status;
      setScopeError(
        typeof status === 'number'
          ? `Could not save boundaries (server responded ${status}).`
          : 'Could not save boundaries. Please check your connection and try again.',
      );
    }
  };

  // Step 3: POST the organization level hierarchy, then advance.
  const saveLevels = async () => {
    setLevelsError(null);
    if (!organizationId) {
      setLevelsError('Please complete step 1 (create the organization) first.');
      return;
    }
    if (levelsData.length === 0) {
      setLevelsError('Add at least one level to continue.');
      return;
    }
    try {
      await createLevelsBulk({
        organization: organizationId,
        replace: true,
        levels: levelsData.map((l) => ({ id: l.id, name: l.name, parentId: l.parentId })),
      }).unwrap();
      goToStep(3);
    } catch (err) {
      const status = (err as { status?: number | string })?.status;
      setLevelsError(
        typeof status === 'number'
          ? `Could not save levels (server responded ${status}).`
          : 'Could not save levels. Please check your connection and try again.',
      );
    }
  };

  const goPrimary = () => {
    if (step === 0) {
      void saveOrganization();
      return;
    }
    if (step === 1) {
      void confirmBoundary();
      return;
    }
    if (step === 2) {
      void saveLevels();
      return;
    }
    if (isLast) finish(formik.values);
    else goToStep(Math.min(LAST_STEP, step + 1));
  };

  return (
    <div className="flex flex-col gap-8">
      {/* Breadcrumb + heading */}
      <div className="flex flex-col gap-1">
        <Breadcrumbs
          items={[
            { label: 'Organizations', to: '/admin/organizations' },
            { label: isEdit && step === 0 ? 'Edit' : meta.label },
          ]}
        />
        <h1 className="pt-1 text-2xl font-semibold tracking-[-0.24px] text-ink-900">
          {isEdit && step === 0 ? 'Edit Organization' : meta.title}
        </h1>
        <p className="text-sm text-ink-secondary">{meta.subtitle}</p>
      </div>

      {/* Stepper — clickable once the org exists; in the create flow you can only go back
          (pending steps stay locked), while edit mode allows jumping to any step. */}
      <OrgWizardStepper
        activeStep={step}
        onStepClick={hasId ? goToStep : undefined}
        visibleSteps={STEPS_META.length}
        hidePending={hasId}
        disablePending={!isEdit}
      />

      {/* Step panels — kept mounted so entered values survive navigation */}
      <div className={step === 0 ? '' : 'hidden'}>
        <OrganizationDetailsStep formik={formik} />
      </div>
      <div className={step === 1 ? '' : 'hidden'}>
        <DefineBoundaryStep
          active={step >= 1}
          onSelectionChange={setLiveSelection}
          onAreasChange={setLiveAreas}
          preselectedAreas={preselectedAreas}
        />
      </div>
      <div className={step === 2 ? '' : 'hidden'}>
        <BuildHierarchyStep
          selection={confirmedSelection.length > 0 ? confirmedSelection : liveSelection}
          onLevelsChange={setHierarchyLevels}
          onLevelsDataChange={setLevelsData}
          initialLevels={initialLevels}
        />
      </div>
      <div className={step === 3 ? '' : 'hidden'}>
        <DataImportStep
          active={step === 3}
          onConfirm={() => finish(formik.values)}
          hierarchyLevels={hierarchyLevels}
          selection={confirmedSelection.length > 0 ? confirmedSelection : liveSelection}
          organizationId={organizationId}
        />
      </div>

      {/* API errors (create-org / save-boundaries / save-levels failed) */}
      {((step === 0 && saveError) || (step === 1 && scopeError) || (step === 2 && levelsError)) && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-[#ba1a1a]">
          {step === 0 ? saveError : step === 1 ? scopeError : levelsError}
        </p>
      )}

      {/* Wizard navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => goToStep(Math.max(0, step - 1))}
          disabled={step === 0}
          className="flex h-10 items-center justify-center rounded-lg border border-ink-secondary px-[21px] text-xs font-medium tracking-[0.6px] text-ink-secondary transition-colors hover:bg-brand-shade disabled:invisible"
        >
          Back
        </button>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/organizations')}
            className="flex h-10 items-center justify-center rounded-lg border border-ink-secondary px-[21px] text-xs font-medium tracking-[0.6px] text-ink-secondary transition-colors hover:bg-brand-shade"
          >
            Cancel
          </button>
          {/* The last step (Data Import) provides its own "Confirm Selection" action. */}
          {!isLast && (
            <button
              type="button"
              onClick={goPrimary}
              disabled={
                (step === 0 && isSaving) ||
                (step === 1 && isSavingScope) ||
                (step === 2 && isSavingLevels)
              }
              className="flex h-10 items-center justify-center gap-2 rounded-lg bg-brand px-5 text-xs font-medium tracking-[0.6px] text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
            >
              {step === 0 && <SaveIcon className="size-[13.5px]" />}
              {step === 0
                ? isSaving
                  ? hasId
                    ? 'Updating…'
                    : 'Saving…'
                  : hasId
                    ? 'Update Organization'
                    : 'Save Organization'
                : step === 1
                  ? isSavingScope
                    ? 'Confirming…'
                    : 'Confirm Selection'
                  : step === 2
                    ? isSavingLevels
                      ? 'Saving…'
                      : 'Save'
                    : 'Save'}
              {step === 1 && !isSavingScope && <CheckCircleIcon className="size-4" />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
